import React, { useState, useRef, useContext, useEffect, useCallback, useMemo } from 'react';
import { UserAuth } from '../context/AuthContext';
import Webcam from "react-webcam";
import ProfileContext from '../context/ProfileContext';
import { useDropzone } from 'react-dropzone';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import whatsappIcon from '../assets/whatsapp.svg'
import { Button } from 'react-bootstrap';
import imageCompression from 'browser-image-compression';
import * as Sentry from '@sentry/react';
import * as faceapi from '@vladmandic/face-api';
import { useUserContext } from '../context/UserContext';
import { CompleteProfile } from './CompleteProfile';
import { useStepContext } from '../context/StepContext';
import { computeFreezeStatus } from '../constants/frozenFields';

const FACE_VERIFICATION_TIMEOUT_MS = 5000;

const withTimeout = (promise, ms, timeoutMessage) =>
  Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error(timeoutMessage)), ms);
    }),
  ]);

export const ProfileVerify = () => {
  const { userData, handleUserChange } = useUserContext();
  const navigate = useNavigate();
  const location = useLocation();
  const isRevalidationMode = location.state?.revalidationMode === true
    || sessionStorage.getItem('revalidationMode') === 'true';
  if (location.state?.revalidationMode === true) {
    sessionStorage.setItem('revalidationMode', 'true');
  }
  const isEditMode = location.state?.editMode === true
    || sessionStorage.getItem('editMode') === 'true';
  if (location.state?.editMode === true) {
    sessionStorage.setItem('editMode', 'true');
  }
  const editStartStep = location.state?.editStartStep || Number(sessionStorage.getItem('editStartStep')) || 1;
  if (location.state?.editStartStep) {
    sessionStorage.setItem('editStartStep', String(location.state.editStartStep));
  }
  // isReviewMode covers both revalidation and edit for shared UI (pre-fill, Next buttons, headings)
  const isReviewMode = isRevalidationMode || isEditMode;
  const { user } = UserAuth();
  const webcamRef = useRef(null);
  const [imgSrc, setImgSrc] = useState("");
  const [showCamera, setShowCamera] = useState(false);
  const [hasPermission, setHasPermission] = useState(null);
  const { profile, setProfile } = useContext(ProfileContext);
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [isPhotoLoading, setPhotoLoading] = useState(false);
  const [isResumeLoading, setResumeLoading] = useState(false);
  const [isMobileVerificationLoading, setMobileVerificationLoading] = useState(false);
  const isAnyStep1OperationLoading = isPhotoLoading || isResumeLoading || isMobileVerificationLoading;
  const [currInx, setIdx] = useState(0);
  const [phone, setPhone] = useState("");
  const [iswhatsappNumber, setIswhatsappNumber] = useState("");
  const [country, setCountry] = useState("");
  const [otp, setOtp] = useState(new Array(4).fill(''));
  const [selectedOption, setSelectedOption] = useState('');
  const [getcameraPermissiontext, setcameraPermissiontext] = useState('Camera permission denied!');
  const [photoUploadProgress, setPhotoUploadProgress] = useState(0);
  const [resumeUploadProgress, setResumeUploadProgress] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [currentToastId, setCurrentToastId] = useState(null);
  const hasScheduledInterview = useRef(false);
  const isSchedulingRef = useRef(false);
  const faceApiModelsLoaded = useRef(false);
  const faceApiInitPromise = useRef(null);
  const faceVerificationDisabled = useRef(false);
  const frozenResumeSyncedRef = useRef(false);
  // Original values for edit mode change detection
  const originalStep1Ref = useRef({
    photoUrl: '',
    resumeUrl: '',
    mobileNumber: '',
  });
  // Accordion state for Step 1 (photo + resume + mobile)
  const [newPhotoCaptured, setNewPhotoCaptured] = useState(false);
  const [resumeUploaded, setResumeUploaded] = useState(false);
  // flat form — no accordion state needed
  // Resume-from-where-left-off: loading gate until candidateInfo is fetched and resume point computed
  const [profileInfoLoading, setProfileInfoLoading] = useState(true);
  const resumePointApplied = useRef(false);
  // Step management state
  // const [currentStep, setCurrentStep] = useState(1);
  const { currentStep, setCurrentStep, totalSteps, stepLabels } = useStepContext();
  const [isNumVerificationCompleted, setIsNumVerificationCompleted] = useState(true);
  const [isOTPVerificationCompleted, setIsOTPVerificationCompleted] = useState(false);
  const [isVerificationCompleted, setIsVerificationCompleted] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const sentryUserEmailRef = useRef('Unknown Email');

  useEffect(() => {
    if (user?.email) {
      sentryUserEmailRef.current = user.email;
      try {
        Sentry.setUser({ email: user.email });
      } catch (_) { /* Sentry unavailable */ }
    }
  }, [user?.email]);

  const logFieldToSentry = (fieldName, value) => {
    try {
      Sentry.captureMessage(`User filled field: ${fieldName} (User: ${sentryUserEmailRef.current?.replace('@', ' at ')})`, {
        level: 'info',
        extra: { value, email: sentryUserEmailRef.current }
      });
    } catch (_) { /* Sentry unavailable */ }
  };

  const logSentryException = (error, extra = {}) => {
    try {
      Sentry.captureException(error, { extra: { email: user?.email, ...extra } });
    } catch (_) { /* Sentry unavailable */ }
  };

  useEffect(() => {
    const handleFocusOut = (e) => {
      if (e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT' || e.target.tagName === 'TEXTAREA')) {
        const fieldName = e.target.name || e.target.placeholder || e.target.id || 'unknown_field';
        const isOtpField = String(e.target.name || '').startsWith('ssn-') || /otp/i.test(String(fieldName)) || /otp/i.test(String(e.target.id || ''));
        const isFileInput = e.target.type === 'file';
        let logValue = isOtpField ? '[REDACTED]' : e.target.value;
        if (isFileInput && e.target.files?.[0]) {
          logValue = { fileName: e.target.files[0].name, fileSize: e.target.files[0].size };
        }
        logFieldToSentry(fieldName, logValue);
      }
    };
    document.addEventListener('focusout', handleFocusOut);
    return () => {
      document.removeEventListener('focusout', handleFocusOut);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const withCacheBuster = (url, versionKey) => {
    if (!url || typeof url !== 'string') return '';
    // Keep local preview URLs untouched.
    if (url.startsWith('blob:') || url.startsWith('data:')) return url;
    const separator = url.includes('?') ? '&' : '?';
    const version = encodeURIComponent(versionKey || Date.now());
    return `${url}${separator}v=${version}`;
  };

  // Freeze logic: compute from schedule_info in profile context
  const scheduleInfo = profile?.userfetchedData?.schedule_info || null;
  const { isFrozen: isHrFrozen } = computeFreezeStatus(scheduleInfo);

  // HR-frozen number verification status from candidateInfo — must be declared BEFORE useMemo blocks that reference it
  const [hrNumVerified, setHrNumVerified] = useState(false);

  // ── Computed states for Step 1 validation & change detection ──

  const isMobileVerified = useMemo(() => {
    // HR-frozen: number is verified if hrNumVerified or OTP completed
    if (isHrFrozen && scheduleInfo?.contact_no) {
      return !!(hrNumVerified || isVerificationCompleted);
    }
    // Normal flow: OTP must be verified
    return !!isVerificationCompleted;
  }, [isHrFrozen, scheduleInfo, hrNumVerified, isVerificationCompleted]);

  const isPhotoValid = useMemo(() => {
    // Photo uploaded AND no face detection error
    return !!imgSrc && !photoError;
  }, [imgSrc, photoError]);

  const isStep1Valid = useMemo(() => {
    // All 3 conditions must be simultaneously true:
    // 1. Photo uploaded + no face detection error
    // 2. Resume uploaded successfully (HR frozen gets auto-resume)
    // 3. Mobile OTP verified
    return isPhotoValid && resumeUploaded && isMobileVerified;
  }, [isPhotoValid, resumeUploaded, isMobileVerified]);
  const hasStep1Changed = useMemo(() => {
    if (!isEditMode) return false;
    const orig = originalStep1Ref.current;
    const photoChanged = newPhotoCaptured;
    const resumeChanged = file !== null;
    const mobileChanged = phone !== orig.mobileNumber;
    return photoChanged || resumeChanged || mobileChanged;
  }, [isEditMode, newPhotoCaptured, file, phone]);

  useEffect(() => {
    const fetchedData = profile?.userfetchedData;
    if (isHrFrozen && scheduleInfo?.contact_no) {
      setHrNumVerified(fetchedData?.is_number_verified === 1);
    }
  }, [profile?.userfetchedData, isHrFrozen, scheduleInfo]);

  // Pre-fill phone from schedule_info for frozen candidates (works in all modes)
  useEffect(() => {
    if (isHrFrozen && scheduleInfo?.contact_no) {
      const frozenPhone = String(scheduleInfo.contact_no).replace(/[^\d]/g, '');
      setPhone(frozenPhone);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHrFrozen, scheduleInfo]);

  const nextStep = () => {
    dismissCurrentToast();
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
    }
  };

  // Clean up mode flags when navigating away mid-flow (browser back, route change)
  useEffect(() => {
    return () => {
      sessionStorage.removeItem('editMode');
      sessionStorage.removeItem('editStartStep');
      sessionStorage.removeItem('revalidationMode');
    };
  }, []);

  // Current Step check
  useEffect(() => {
    if (isEditMode) {
      setCurrentStep(editStartStep);
    } else if (!isRevalidationMode && userData?.currentStep !== undefined) {
      setCurrentStep(userData?.currentStep);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.currentStep, isRevalidationMode, isEditMode, editStartStep])
  useEffect(() => {
    if (!isReviewMode) return;
    const fetchedData = profile?.userfetchedData;
    if (!fetchedData) return;
    // Pre-fill photo from latest backend fields in priority order.
    const backendPhoto = fetchedData.user_photo || fetchedData.profile_img || '';
    const photoVersion = fetchedData.last_data_updated_at || fetchedData.updated_at || Date.now();
    setImgSrc(withCacheBuster(backendPhoto, photoVersion));
    // In review mode, photo is already on server — unlock resume accordion
    setResumeUploaded(true); // unlock mobile accordion in review mode
    // Pre-fill phone: prefer schedule_info contact_no when HR-frozen
    const sInfo = fetchedData.schedule_info;
    const frozenPhone = isHrFrozen && sInfo?.contact_no
      ? String(sInfo.contact_no).replace(/[^\d]/g, '')
      : null;
    if (frozenPhone) {
      setPhone(frozenPhone);
    } else if (fetchedData.contact_no) {
      const raw = String(fetchedData.contact_no).replace(/[^\d]/g, '');
      setPhone(raw);
    }
    // Capture original values for edit mode Step 1 change detection
    if (isEditMode) {
      originalStep1Ref.current = {
        photoUrl: backendPhoto || '',
        resumeUrl: fetchedData.resume_url || '',
        mobileNumber: (frozenPhone || (fetchedData.contact_no ? String(fetchedData.contact_no).replace(/[^\d]/g, '') : '')),
      };
    }
  }, [isReviewMode, profile?.userfetchedData, isHrFrozen, isEditMode]);

  // Step 1 edit mode: always fetch fresh candidateInfo so latest user_photo is shown.
  useEffect(() => {
    if (!isEditMode || editStartStep !== 1 || !user?.email) return;
    let isCancelled = false;

    const fetchLatestForStep1Edit = async () => {
      try {
        const refreshedToken = await user?.getIdToken();
        if (!refreshedToken) return;

        const response = await axios.post(
          `${process.env.REACT_APP_FRONTEND}/api/v4.0/candidate-info/fetchuser`,
          {
            operation: 'fetchuser',
            email: user?.email,
          },
          {
            headers: {
              'Authorization': `Bearer ${refreshedToken}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (isCancelled) return;

        const latestData = response?.data?.data?.[0];
        if (!latestData) return;

        setProfile((prev) => ({
          ...prev,
          userfetchedData: latestData,
        }));
        const latestPhoto = latestData.user_photo || latestData.profile_img || '';
        const latestPhotoVersion = latestData.last_data_updated_at || latestData.updated_at || Date.now();
        setImgSrc(withCacheBuster(latestPhoto, latestPhotoVersion));
      } catch (error) {
        logSentryException(error);
        console.error('Failed to fetch latest profile data on step 1 edit:', error);
      }
    };

    fetchLatestForStep1Edit();
    return () => { isCancelled = true; };
  }, [isEditMode, editStartStep, setProfile, user]);

  // Resume from where the user left off (non-review/non-edit mode only).
  // Runs once after candidateInfo is available; falls back to a direct fetch on page refresh.
  useEffect(() => {
    if (isReviewMode) {
      setProfileInfoLoading(false);
      return;
    }
    if (resumePointApplied.current) return;

    const applyResumePoint = async () => {
      let data = profile?.userfetchedData;

      if (!data) {
        // Fallback: fetch candidateInfo directly (handles page-refresh on /profile-verify)
        try {
          const token = await user?.getIdToken();
          if (!token) { setProfileInfoLoading(false); return; }
          const res = await axios.post(
            `${process.env.REACT_APP_FRONTEND}/api/v4.0/candidate-info/fetchuser`,
            { operation: 'fetchuser', email: user?.email },
            { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
          );
          data = res.data.data?.[0] || null;
        } catch {
          toast.error('Could not load your profile. Starting from the beginning.');
          setProfileInfoLoading(false);
          return;
        }
      }

      if (!data) { setProfileInfoLoading(false); return; }

      resumePointApplied.current = true;

      // Strict checks: all three must be true to go to Step 2
      const hasPhoto = !!(data?.user_photo?.trim());
      const hasResume = !!(data?.resume_url?.trim());
      const isNumberVerified = data?.is_number_verified === 1;

      const shouldGoToStep2 = hasPhoto && hasResume && isNumberVerified;

      // Priority order: highest completion first
      if (data.resume_headline_added === 1) {
        navigate('/download');
        return;
      }
      if (data.is_edu_added === 1 && shouldGoToStep2) {
        setResumeUploaded(true);
        setIsVerificationCompleted(true);
        setCurrentStep(2);
      } else if (data.is_employement_added === 1 && shouldGoToStep2) {
        setResumeUploaded(true);
        setIsVerificationCompleted(true);
        setCurrentStep(2);
      } else if (shouldGoToStep2) {
        // All 3 Step 1 conditions met → go to Step 2
        setResumeUploaded(true);
        setIsVerificationCompleted(true);
        setCurrentStep(2);
      } else if (hasPhoto && hasResume) {
        // Photo + Resume done but mobile not verified → stay on Step 1, show mobile section
        setResumeUploaded(true);
      } else if (hasPhoto) {
        // Photo done — show resume section
      }
      // else: fresh user — default (step 1, all locked)

      setProfileInfoLoading(false);
    };

    applyResumePoint();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.userfetchedData]);

  // Upload constants
  const UPLOAD_TIMEOUT = 120000; // 2 minutes
  const MAX_RETRY_ATTEMPTS = 3;
  const RETRY_DELAY = 2000; // 2 seconds

  // FIXED: Phone number formatting function
  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    let cleaned = phoneNumber.replace(/\D/g, '');
    if (cleaned.startsWith('00')) {
      cleaned = cleaned.substring(2);
    }
    if (cleaned.length > 10) {
      return '+' + cleaned;
    }
    return '+' + cleaned;
  };

  // FIXED: Enhanced phone number validation
  const validatePhoneNumber = (phoneNumber) => {
    const formatted = formatPhoneNumber(phoneNumber);

    // Basic format check
    if (!formatted || !formatted.startsWith('+')) {
      return false;
    }

    const digitsOnly = formatted.substring(1);
    if (!/^\d+$/.test(digitsOnly)) {
      return false;
    }

    // Indian numbers: +91 followed by exactly 10 digits
    if (digitsOnly.startsWith('91')) {
      return formatted.length === 13 && digitsOnly.length === 12;
    }
    // US/Canada: +1 followed by exactly 10 digits
    else if (digitsOnly.startsWith('1')) {
      return formatted.length === 12 && digitsOnly.length === 11;
    }
    // UK: +44 followed by 10-11 digits
    else if (digitsOnly.startsWith('44')) {
      return formatted.length >= 13 && formatted.length <= 14;
    }
    // China: +86 followed by exactly 11 digits
    else if (digitsOnly.startsWith('86')) {
      return formatted.length === 14 && digitsOnly.length === 13;
    }

    // Generic fallback for other countries
    return formatted.length >= 10 && formatted.length <= 16;
  };

  // Enhanced client-side validation for resume files
  const validateResumeFile = (file) => {
    const errors = [];

    const allowedMimeTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];

    const allowedExtensions = ['.pdf', '.doc', '.docx'];
    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.substring(fileName.lastIndexOf('.'));

    const hasValidExtension = allowedExtensions.includes(fileExtension);
    const hasValidMimeType = allowedMimeTypes.includes(file.type);

    if (!hasValidExtension && !hasValidMimeType) {
      errors.push(`Invalid file type "${fileExtension}". Only PDF, DOC, and DOCX files are allowed.`);
    }

    const maxSize = isMobile ? 3 * 1024 * 1024 : 5 * 1024 * 1024; // 3MB mobile, 5MB desktop
    const minSize = 1024; // 1KB minimum

    if (file.size > maxSize) {
      const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(1);
      errors.push(`File size ${(file.size / (1024 * 1024)).toFixed(1)}MB is too large. Maximum ${maxSizeMB}MB allowed.`);
    }

    if (file.size < minSize) {
      errors.push('File size too small. Please select a valid resume file.');
    }

    if (file.name.length > 150) {
      errors.push('File name too long. Please rename the file to under 150 characters.');
    }

    const problematicChars = /[<>:"|?*]/;
    if (problematicChars.test(file.name)) {
      errors.push('File name contains invalid characters. Please rename the file.');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  };

  // Enhanced error messages for different scenarios
  const getUploadErrorMessage = (error, isMobile) => {
    const message = error.message.toLowerCase();

    if (message.includes('both') && message.includes('apis failed')) {
      return isMobile ?
        'Upload failed. Check connection and try again.' :
        'Upload failed with both methods. Please check your connection and try again.';
    }

    if (message.includes('timeout') || message.includes('network')) {
      return isMobile ?
        'Connection timeout. Try again.' :
        'Connection timeout. Please check your network and try again.';
    }

    if (message.includes('size') || message.includes('too large')) {
      const maxSize = isMobile ? '3MB' : '5MB';
      return `File too large. Maximum ${maxSize} allowed.`;
    }

    if (message.includes('type') || message.includes('format') || message.includes('invalid')) {
      return 'Invalid file format. Use PDF, DOC, or DOCX only.';
    }

    if (message.includes('corrupt') || message.includes('damaged')) {
      return 'File appears to be corrupted. Please try a different file.';
    }

    if (message.includes('server') || message.includes('500')) {
      return isMobile ?
        'Server error. Please try again.' :
        'Server error occurred. Please try again in a moment.';
    }

    return isMobile ?
      'Upload failed. Try again.' :
      'Upload failed. Please check your file and try again.';
  };

  // Toast management functions
  const dismissCurrentToast = () => {
    if (currentToastId) {
      toast.dismiss(currentToastId);
      setCurrentToastId(null);
    }
  };

  const showToast = (type, message, options = {}) => {
    dismissCurrentToast();

    const toastOptions = {
      ...getMobileToastConfig(),
      ...options
    };

    let toastId;
    switch (type) {
      case 'success':
        toastId = toast.success(message, toastOptions);
        break;
      case 'error':
        toastId = toast.error(message, toastOptions);
        break;
      case 'info':
        toastId = toast.info(message, toastOptions);
        break;
      case 'warning':
        toastId = toast.warning(message, toastOptions);
        break;
      default:
        toastId = toast(message, toastOptions);
    }

    setCurrentToastId(toastId);
    return toastId;
  };

  // Mobile detection and viewport setup
  useEffect(() => {
    const viewport = document.querySelector("meta[name=viewport]");
    if (!viewport) {
      const meta = document.createElement('meta');
      meta.name = 'viewport';
      meta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const userValue = Object.keys(user).length;
    if (userValue === 0) {
      navigate('/')
    }
    showSection(currInx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Network status monitoring
  useEffect(() => {
    const handleOnline = () => {
      showToast('success', 'Internet connection restored!');
    };

    const handleOffline = () => {
      showToast('warning', 'Internet connection lost. Please check your network.', { autoClose: false });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  // Cleanup webcam on unmount
  useEffect(() => {
    return () => {
      dismissCurrentToast();
      stopWebcamStream();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mobile-optimized configuration functions
  const getMobileToastConfig = () => ({
    autoClose: isMobile ? 4000 : 3000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: !isMobile,
    draggable: !isMobile,
    style: isMobile ? {
      fontSize: '14px',
      borderRadius: '8px',
      margin: '0 10px 10px 10px'
    } : {}
  });

  const getMobileWebcamConstraints = () => {
    if (isMobile) {
      return {
        facingMode: "user",
        width: { ideal: 480, max: 640 },
        height: { ideal: 360, max: 480 }
      };
    }
    return {
      facingMode: "user",
      width: { ideal: 640 },
      height: { ideal: 480 }
    };
  };

  const getMobileFileSizeLimit = () => {
    if (isMobile) {
      return 2 * 1024 * 1024; // 2MB for mobile
    }
    return 5 * 1024 * 1024; // 5MB for desktop
  };

  const getButtonClasses = (baseClasses) => {
    return `${baseClasses} ${isMobile ? 'mobile-btn' : ''}`;
  };

  const handleMobileErrors = (error) => {
    if (isMobile) {
      if (error.message.includes('timeout')) {
        return 'Upload timed out. Check your connection.';
      } else if (error.message.includes('network')) {
        return 'Network error. Please try again.';
      } else if (error.message.includes('camera')) {
        return 'Camera access denied. Please allow camera in browser settings.';
      }
      return 'Something went wrong. Please try again.';
    }
    return error.message;
  };

  const handleOnChange = (value, country) => {
    setPhone(value);
    setCountry(country);
    setError('');
    if (value) logFieldToSentry('Mobile number', value);
    if (value && value.length > 5) {
      const isValid = validatePhoneNumber(value);
      if (!isValid) {
        console.warn('Phone number format may be invalid:', value);
      }
    }
  };

  const handleDrop = useCallback((acceptedFiles, rejectedFiles) => {
    setError("");
    setResumeLoading(false);

    const allFiles = [...(acceptedFiles || []), ...(rejectedFiles?.map(r => r.file) || [])];

    if (allFiles.length === 0) {
      const errorMessage = isMobile ? 'No files selected' : 'No files selected. Please choose a file.';
      setError(errorMessage);
      showToast('error', errorMessage);
      return;
    }

    let selectedFile = null;

    if (acceptedFiles && acceptedFiles.length > 0) {
      selectedFile = acceptedFiles[0];
    } else if (rejectedFiles && rejectedFiles.length > 0) {
      selectedFile = rejectedFiles[0].file;
    }

    if (!selectedFile) {
      const errorMessage = 'Unable to process the selected file.';
      setError(errorMessage);
      showToast('error', errorMessage);
      return;
    }

    const validation = validateResumeFile(selectedFile);

    if (!validation.isValid) {
      const errorMessage = validation.errors[0];
      setError(errorMessage);
      showToast('error', errorMessage);
      return;
    }

    setFile(selectedFile);
    setResumeUploaded(false); // new file selected, mark as not yet uploaded
    setError('');
    logFieldToSentry('Resume upload', { fileName: selectedFile.name, fileSize: selectedFile.size });

    // Immediately trigger upload on file selection — pass file directly to avoid stale state
    uploadResume(null, selectedFile);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  function Dropzone({ onDrop }) {
    const onDropCallback = useCallback((acceptedFiles, rejectedFiles) => {
      onDrop(acceptedFiles, rejectedFiles);
    }, [onDrop]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      onDrop: onDropCallback,
      maxSize: isMobile ? 3 * 1024 * 1024 : 5 * 1024 * 1024,
      multiple: false,
      validator: null,
      accept: {
        'application/pdf': ['.pdf'],
        'application/msword': ['.doc'],
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
        '*': ['.pdf', '.doc', '.docx']
      }
    });

    return (
      <div {...getRootProps()} className={`dropzone-container bg-warning bg-opacity-10 border-primary border-opacity-25 rounded-4 ${isMobile ? 'mobile-dropzone' : ''} ${isDragActive ? 'drag-active' : ''}`}>
        <input {...getInputProps()} />
        {isDragActive ? (
          <div className="fs-14 text-dark mb-1">{isMobile ? 'Drop file here...' : 'Drop the files here ...'}</div>
        ) : (
          <div className='text-muted'>
            <div className="fs-14 text-dark mb-1">{isMobile ? 'Tap to browse files' : 'Drop your file here or Browse.'}</div>
            <div className="fs-12 text-muted mb-2 pb-1">{isMobile ? 'PDF, DOC, DOCX only' : 'File should be (DOCX, PDF, DOC) only'}</div>
            <Button variant='theme6' type="button" className="text-white">Browse File</Button>
          </div>
        )}
      </div>
    );
  }

  const renderFileStatus = () => {
    if (file) {
      const fileSizeKB = Math.round(file.size / 1024);
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      const displaySize = fileSizeKB > 1024 ? `${fileSizeMB}MB` : `${fileSizeKB}KB`;

      return (
        <div className="file-status mt-3">
          <div className='bg-light border py-2 px-3 rounded-pill small text-truncate mx-auto d-flex align-items-center justify-content-center'
            style={{ maxWidth: isMobile ? '280px' : '320px' }}>
            <i className="bi bi-file-earmark-text me-2 text-success"></i>
            <span className="text-truncate">
              {file.name} ({displaySize})
            </span>
          </div>
          {resumeUploaded && (
            <div className="text-success small mt-2">
              <i className="bi bi-check-circle-fill me-1"></i>
              Resume uploaded successfully
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  const handleUserMedia = stream => {
    setHasPermission(true);
  };

  const handleUserMediaError = () => {
    setHasPermission(false);
    setShowCamera(false);
  };

  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const uploadWithRetry = async (url, formData, headers, attempt = 1, useFallback = false, onProgress) => {
    try {
      if (attempt > 1) {
        showToast('info', `Retrying upload... (${attempt}/${MAX_RETRY_ATTEMPTS})`);
      }

      const response = await axios.post(url, formData, {
        headers,
        timeout: UPLOAD_TIMEOUT,
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(percentCompleted);
          }
        }
      });

      return response;
    } catch (error) {
      logSentryException(error);
      console.error(`Upload attempt ${attempt} failed:`, error.message);

      const shouldRetry = attempt < MAX_RETRY_ATTEMPTS && (
        error.code === 'ECONNABORTED' ||
        error.code === 'NETWORK_ERROR' ||
        error.response?.status >= 500 ||
        error.response?.status === 429 ||
        !navigator.onLine
      );

      if (shouldRetry) {
        await delay(RETRY_DELAY * attempt);
        return uploadWithRetry(url, formData, headers, attempt + 1, useFallback, onProgress);
      }

      throw error;
    }
  };

  async function generateOtp() {
    if (!validatePhoneNumber(phone)) {
      showToast('error', "Please enter your valid phone number.");
      return;
    }
    if (!phone || phone.trim() === "") {
      showToast('error', "Please enter your phone number.");
      return;
    }

    if (iswhatsappNumber === "") {
      showToast('error', isMobile ? "Please confirm WhatsApp usage" : "Can you confirm if you are using this number for WhatsApp?");
      return;
    }

    let loadingToastId = null;

    try {
      const formattedPhone = formatPhoneNumber(phone);

      if (!validatePhoneNumber(formattedPhone)) {
        showToast('error', 'Please enter a valid phone number format.');
        return;
      }

      setMobileVerificationLoading(true);
      loadingToastId = showToast('info', 'Validating phone number...', { autoClose: false });

      if (loadingToastId) {
        toast.dismiss(loadingToastId);
        setCurrentToastId(null);
      }

      loadingToastId = showToast('info', 'Sending OTP...', { autoClose: false });
      const refreshedToken = await user?.getIdToken();
      const headers = {
        'Authorization': `Bearer ${refreshedToken}`,
        'Content-Type': 'application/json',
      };

      const submitdata = {
        login_token: user?.uid,
        to: formattedPhone,
        operation: 'sendotp',
        is_whatsapp: iswhatsappNumber === "Yes" ? 1 : 0,
        app_version: `${process.env.REACT_APP_VERSION}`
      };
      handleUserChange("contactNumber", formattedPhone)

      const response = await axios.post(
        `${process.env.REACT_APP_APIURL_NMVE}/v3.0/otp/sendForWeb`,
        submitdata,
        {
          headers: headers,
          timeout: 20000
        }
      );

      if (loadingToastId) {
        toast.dismiss(loadingToastId);
        setCurrentToastId(null);
        loadingToastId = null;
      }

      if (response?.data.statusCode === 400) {
        setMobileVerificationLoading(false);
        const showMsg = response?.data.errorMessage || 'Failed to send OTP';
        showToast('error', showMsg);
      } else {
        setMobileVerificationLoading(false);
        showToast('success', 'OTP Sent Successfully!');
        setIsNumVerificationCompleted(false);
        setIsOTPVerificationCompleted(true);
      }
    } catch (error) {
      logSentryException(error);
      if (loadingToastId) {
        toast.dismiss(loadingToastId);
        setCurrentToastId(null);
      }
      setMobileVerificationLoading(false);
      console.error('Generate OTP error:', error);

      let errorMessage = 'Network error. Please try again.';

      if (error.code === 'ECONNABORTED') {
        errorMessage = 'Request timeout. Please check your connection and try again.';
      } else if (error.response?.status >= 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication error. Please refresh and try again.';
      }

      showToast('error', errorMessage);
    } finally {
      if (loadingToastId) {
        toast.dismiss(loadingToastId);
        setCurrentToastId(null);
      }
    }
  }

  const handleChange = (event, index) => {
    const newOtp = [...otp];
    const { maxLength, value, name } = event.target;

    newOtp[index] = value;
    setOtp(newOtp);
    if (value) {
      logFieldToSentry('OTP', '[REDACTED]');
    }

    const [, fieldIndex] = name.split("-");

    if (value.length >= maxLength) {
      if (parseInt(fieldIndex, 10) < 4) {
        const nextSibling = document.querySelector(`input[name=ssn-${parseInt(fieldIndex, 10) + 1}]`);
        if (nextSibling !== null) {
          nextSibling.focus();
        }
      }
    }
  };

  const handleKeyDown = (event, index) => {
    if (event.key === 'Backspace') {
      const newOtp = [...otp];

      if (newOtp[index] !== '') {
        newOtp[index] = '';
        setOtp(newOtp);
      }
      else if (index > 0) {
        newOtp[index - 1] = '';
        setOtp(newOtp);
        const prevSibling = document.querySelector(`input[name=ssn-${index}]`);
        if (prevSibling !== null) {
          prevSibling.focus();
        }
      }
    }
  };

  const getInterviewdata = async (url, payload, getToken) => {
    if (isReviewMode) return
    const refreshedToken = getToken;
    const headers = {
      'Authorization': `Bearer ${refreshedToken}`,
      'Content-Type': 'application/json',
    };
    try {
      const response = await axios.post(url, payload, {
        headers: headers,
      });
      if (response) {
        const getData = response.data
        if (getData) {
          // sheelaQuestionGenerate handled here if needed
        }
      }
      return response.data;
    } catch (error) {
      logSentryException(error);
      console.error("Error posting data:", error);
      throw error;
    }
  };

  const handleSubmit = async (event) => {
    try {
      event.preventDefault();
      const enteredOtp = otp.join('');
      if (enteredOtp?.length !== 4) {
        showToast('error', 'Please enter a 4-digit OTP!');
      } else {
        setMobileVerificationLoading(true);

        const verifyingToastId = showToast('info', 'Verifying OTP...', { autoClose: false });

        const refreshedToken = await user?.getIdToken();
        const headers = {
          'Authorization': `Bearer ${refreshedToken}`,
          'Content-Type': 'application/json',
        };

        const submitdata = {
          operation: 'verify',
          login_token: user?.uid,
          otp: enteredOtp,
        };

        const response = await axios.post(`${process.env.REACT_APP_APIURL_NMVE}/v3.0/otp/verifyOTPForWeb`, submitdata, {
          headers: headers,
        });

        if (verifyingToastId) {
          toast.dismiss(verifyingToastId);
          setCurrentToastId(null);
        }

        setIsNumVerificationCompleted(false);

        await new Promise(resolve => setTimeout(resolve, 100));
        setMobileVerificationLoading(false);
        const dataVal = response.data;
        if (dataVal?.verified) {
          showToast('success', 'OTP Verified!');
          setIsOTPVerificationCompleted(false);
          setIsVerificationCompleted(true);

          const getToken = dataVal?.token
          handleUserChange("otpToken", getToken);
        } else if (dataVal?.statusCode === 429) {
          showToast('error', dataVal?.errorMessage || 'Too many failed OTP attempts. Please try again later.');
          setIsOTPVerificationCompleted(true);
        } else {
          const attemptsMsg = dataVal?.attemptsRemaining !== undefined
            ? ` ${dataVal.attemptsRemaining} attempt(s) remaining.`
            : '';
          showToast('error', `${dataVal?.errorMessage || 'OTP does not match!'}${attemptsMsg}`);
          setIsOTPVerificationCompleted(true);
        }
      }
    } catch (error) {
      logSentryException(error);
      setMobileVerificationLoading(false);
      console.error(error);
      showToast('error', 'Network error!');
    }
  };

  function showSection(sectionIndex) {
    setIdx(sectionIndex);
  }

  function nextSection() {
    dismissCurrentToast();
    if (currInx < totalSteps - 1) {
      showSection(currInx + 1);
    }
  }

  const dataURItoBlob = async (dataURI) => {
    try {
      const byteString = atob(dataURI.split(',')[1]);
      const arrayBuffer = new ArrayBuffer(byteString.length);
      const uintArray = new Uint8Array(arrayBuffer);
      for (let i = 0; i < byteString.length; i++) {
        uintArray[i] = byteString.charCodeAt(i);
      }
      return new Blob([uintArray], { type: 'image/jpeg' });
    } catch (error) {
      logSentryException(error);
      console.error("Error in dataURItoBlob:", error);
      throw error;
    }
  };

  const compressAndResizeImage = async (file) => {
    try {
      const connectionType = navigator.connection?.effectiveType;
      const isSlowConnection = ['slow-2g', '2g', '3g'].includes(connectionType);

      const options = {
        maxSizeMB: isMobile || isSlowConnection ? 0.5 : 1,
        maxWidthOrHeight: isMobile || isSlowConnection ? 600 : 800,
        useWebWorker: true,
        fileType: 'image/jpeg',
        quality: isMobile || isSlowConnection ? 0.6 : 0.8
      };

      let compressedFile = await imageCompression(file, options);

      if (compressedFile.size < 10 * 1024) {
        const minSize = 50 * 1024;
        const paddingSize = Math.max(0, minSize - compressedFile.size);

        if (paddingSize > 0 && paddingSize < 10 * 1024 * 1024) {
          try {
            const padding = new Uint8Array(paddingSize);
            const dummyBlob = new Blob([compressedFile, padding], { type: 'image/jpeg' });
            return dummyBlob;
          } catch (paddingError) {
            console.warn('Padding failed, returning original compressed file:', paddingError);
            return compressedFile;
          }
        }
      }

      return compressedFile;
    } catch (error) {
      logSentryException(error);
      console.error('Image compression error:', error);
      throw new Error('Failed to compress image');
    }
  };

  const logToSentry = (error, formDataSummary, sentryUser) => {
    try {
      Sentry.withScope(scope => {
        scope.setExtra('payloadData', formDataSummary);
        scope.setUser({
          id: sentryUser?.uid,
          email: sentryUser?.email,
          username: sentryUser?.displayName,
        });
        Sentry.captureException(error);
      });
    } catch (_) { /* Sentry unavailable */ }
  };

  const validateFormDataFields = (formData, requiredKeys) => {
    for (const key of requiredKeys) {
      const value = formData.get(key);
      if (
        value === null ||
        value === undefined ||
        value === '' ||
        (value instanceof Blob && value.size === 0)
      ) {
        return key;
      }
    }
    return null;
  };

  const stopWebcamStream = async () => {
    try {
      if (webcamRef.current && webcamRef.current.video) {
        const mediaStream = webcamRef.current.video.srcObject;
        if (mediaStream) {
          mediaStream.getTracks().forEach(track => {
            track.stop();
          });
          webcamRef.current.video.srcObject = null;
        }
      }

      if (navigator.mediaDevices) {
        await navigator.mediaDevices.enumerateDevices();
      }

      await new Promise(resolve => setTimeout(resolve, 300));

    } catch (error) {
      logSentryException(error);
      console.error('Error stopping webcam stream:', error);
    }
  };

  const loadFaceApiModels = async () => {
    if (faceVerificationDisabled.current) return false;
    if (faceApiModelsLoaded.current) return true;
    if (!faceApiInitPromise.current) {
      faceApiInitPromise.current = (async () => {
        try {
          const webglOk = await faceapi.tf.setBackend('webgl');
          if (!webglOk) await faceapi.tf.setBackend('wasm');
          await faceapi.tf.ready();
          const MODEL_URL = `${process.env.PUBLIC_URL}/models`;
          await faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL);
          faceApiModelsLoaded.current = true;
          return true;
        } catch (err) {
          faceVerificationDisabled.current = true;
          console.warn('Face model load failed; skipping face verification for this session.', err);
          return false;
        }
      })();
    }
    const loaded = await faceApiInitPromise.current;
    if (!loaded) {
      faceApiInitPromise.current = null;
    }
    return loaded;
  };

  const verifyFaceInImage = async (imageSrc, { onVerifying } = {}) => {
    if (faceVerificationDisabled.current) {
      return { skipped: true };
    }

    const modelsReady = await loadFaceApiModels();
    if (!modelsReady) {
      return { skipped: true };
    }

    try {
      const detected = await withTimeout(
        (async () => {
          onVerifying?.();

          const img = new Image();
          img.src = imageSrc;
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
          });

          const detections = await faceapi.detectAllFaces(
            img,
            new faceapi.TinyFaceDetectorOptions({ scoreThreshold: 0.1, inputSize: 224 })
          );
          return detections.length > 0;
        })(),
        FACE_VERIFICATION_TIMEOUT_MS,
        'FACE_VERIFICATION_TIMEOUT'
      );
      return { skipped: false, detected };
    } catch (err) {
      faceVerificationDisabled.current = true;
      faceApiModelsLoaded.current = false;
      faceApiInitPromise.current = null;
      const reason = err?.message === 'FACE_VERIFICATION_TIMEOUT'
        ? 'Face detection timed out; skipping face verification for this session.'
        : 'Face detection failed; skipping face verification for this session.';
      console.warn(reason, err);
      return { skipped: true };
    }
  };

  const capture = async () => {
    if (!webcamRef?.current) {
      showToast('error', 'Webcam not initialized. Please refresh and try again.');
      return;
    }

    let progressToastId = null;

    try {
      setPhotoLoading(true);
      setPhotoUploadProgress(0);
      setPhotoError('');

      progressToastId = showToast('info', isMobile ? 'Uploading photo...' : 'Uploading photo... Please wait', { autoClose: false });

      setHasPermission(true);

      const imageSrc = webcamRef.current.getScreenshot();
      if (!imageSrc) {
        throw new Error('Failed to capture image from webcam');
      }

      if (progressToastId) {
        toast.update(progressToastId, { render: isMobile ? 'Uploading photo...' : 'Uploading photo... Please wait' });
      }
      const faceVerification = await verifyFaceInImage(imageSrc, {
        onVerifying: () => {
          if (progressToastId) {
            toast.update(progressToastId, { render: isMobile ? 'Checking face...' : 'Verifying face... Please wait' });
          }
        },
      });
      if (!faceVerification.skipped && !faceVerification.detected) {
        throw new Error('NO_FACE_DETECTED');
      }

      const blob = await dataURItoBlob(imageSrc);
      if (!blob || blob.size === 0) {
        throw new Error('Failed to process captured image');
      }
      logFieldToSentry('Photo upload', { fileSize: blob.size });

      const sizeLimit = getMobileFileSizeLimit();
      if (blob.size > sizeLimit) {
        const sizeMB = (sizeLimit / (1024 * 1024)).toFixed(1);
        throw new Error(`Image too large. Maximum ${sizeMB}MB allowed.`);
      }

      if (blob.size < 1024) {
        throw new Error('Captured image is too small');
      }

      const compressedBlob = await compressAndResizeImage(blob);

      if (compressedBlob.size === 0) {
        throw new Error('Image compression failed - resulted in empty file');
      }

      if (compressedBlob.size > 10 * 1024 * 1024) {
        throw new Error('Compressed image is still too large');
      }

      const imageName = `${user?.uid}.jpg`;

      let uploadResponse = null;
      let uploadSuccessful = false;

      try {
        const formData = new FormData();
        formData.append("file", compressedBlob);
        formData.append("bucketName", "interviewer-profile");
        formData.append("key", `${user?.uid}/${imageName}`);
        formData.append("contentType", 'image/jpeg');
        formData.append("isEncoding", false);

        const requiredKeys = ["file", "bucketName", "key", "contentType", "isEncoding"];
        const missingKey = validateFormDataFields(formData, requiredKeys);
        if (missingKey) {
          throw new Error(`Missing required field: ${missingKey}`);
        }

        const refreshedToken = await user?.getIdToken();
        const headers = {
          'Authorization': `Bearer ${refreshedToken}`
        };

        const response = await uploadWithRetry(
          `${process.env.REACT_APP_UPLOAD}/api/v3.0/s3/upload`,
          formData,
          headers,
          1,
          false,
          setPhotoUploadProgress
        );

        if (response.status === 200 && response.data?.uploadResponse) {
          uploadResponse = response.data.uploadResponse;
          uploadSuccessful = true;
        } else {
          throw new Error('Primary API upload failed - invalid server response');
        }

      } catch (primaryError) {
        console.error('Primary API failed, trying fallback API:', primaryError.message);

        if (progressToastId) {
          toast.dismiss(progressToastId);
          setCurrentToastId(null);
        }
        progressToastId = showToast('info', 'Trying alternative upload method...', { autoClose: false });

        try {
          const fallbackFormData = new FormData();
          fallbackFormData.append("file", compressedBlob);
          fallbackFormData.append("bucketName", "interviewer-profile");
          fallbackFormData.append("key", `${user?.uid}/${imageName}`);
          fallbackFormData.append("contentType", 'image/jpeg');
          fallbackFormData.append("isEncoding", false);

          const refreshedToken = await user?.getIdToken();
          const fallbackHeaders = {
            'Authorization': `Bearer ${refreshedToken}`
          };

          const fallbackResponse = await uploadWithRetry(
            'https://sheela.io/eye/api/v1.0/system-detail/uploadFileGeneric',
            fallbackFormData,
            fallbackHeaders,
            1,
            true,
            setPhotoUploadProgress
          );

          if (fallbackResponse.status === 200 && fallbackResponse.data?.uploadResponse) {
            uploadResponse = fallbackResponse.data.uploadResponse;
            uploadSuccessful = true;
          } else {
            throw new Error('Fallback API upload failed - invalid server response');
          }

        } catch (fallbackError) {
          console.error('Fallback API also failed:', fallbackError.message);

          const combinedError = new Error(
            `Both upload APIs failed. Primary: ${primaryError.message}. Fallback: ${fallbackError.message}`
          );

          const formDataSummary = {
            primaryError: primaryError.message,
            fallbackError: fallbackError.message,
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
            connectionType: navigator.connection?.effectiveType || 'unknown',
            online: navigator.onLine,
            isMobile: isMobile,
            fileSize: compressedBlob.size,
            fileName: imageName
          };

          logToSentry(combinedError, formDataSummary, user);
          throw combinedError;
        }
      }

      if (uploadSuccessful && uploadResponse) {
        setImgSrc(URL.createObjectURL(compressedBlob));
        setShowCamera(false);

        await stopWebcamStream();

        if (progressToastId) {
          toast.dismiss(progressToastId);
          setCurrentToastId(null);
          progressToastId = null;
        }

        await new Promise(resolve => setTimeout(resolve, 100));

        await saveProfileInfo("updateUserProfile", uploadResponse, "updatePhoto", () => {
          setNewPhotoCaptured(true);
        });
      }

    } catch (error) {
      console.error('Capture error:', error);

      if (progressToastId) {
        toast.dismiss(progressToastId);
        setCurrentToastId(null);
      }

      // Face detection failure — user-facing validation, do not log to Sentry
      if (error.message === 'NO_FACE_DETECTED') {
        setPhotoError('No face detected. Please ensure your face is clearly visible and well-lit, with sufficient lighting on your face so that it can be detected clearly, and try again.');
        return;
      }

      const formDataSummary = {
        error: error.message,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        connectionType: navigator.connection?.effectiveType || 'unknown',
        online: navigator.onLine,
        isMobile: isMobile,
        step: 'photo_upload_with_fallback'
      };
      logToSentry(error, formDataSummary, user);

      let errorMessage = 'Failed to capture photo. Please try again.';

      if (error.message.includes('Both upload APIs failed')) {
        errorMessage = isMobile ?
          'Upload failed. Check connection and try again.' :
          'Upload failed with both methods. Please check your connection and try again.';
      } else if (error.message.includes('compress')) {
        errorMessage = isMobile ? 'Image processing failed. Try again.' : 'Failed to process image. Please try again.';
      } else if (error.message.includes('too small')) {
        errorMessage = 'Captured image is invalid. Please try again.';
      } else if (error.message.includes('too large')) {
        errorMessage = isMobile ? 'Image too large. Adjust lighting.' : 'Image is too large. Please adjust lighting or webcam settings.';
      } else {
        errorMessage = handleMobileErrors(error);
      }

      showToast('error', errorMessage, { autoClose: 6000 });

    } finally {
      setPhotoLoading(false);
      setPhotoUploadProgress(0);

      if (progressToastId) {
        toast.dismiss(progressToastId);
        setCurrentToastId(null);
      }
    }
  };

  const handleTurnOnCamera = async () => {
    try {
      setPhotoLoading(true);

      await stopWebcamStream();

      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');

      if (videoDevices.length === 0) {
        throw new Error('No camera found on your device');
      }

      await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: getMobileWebcamConstraints()
      });

      setHasPermission(true);
      setShowCamera(true);
      setImgSrc("");

      toast.success(isMobile ? 'Camera ready!' : 'Camera activated successfully!', getMobileToastConfig());

    } catch (err) {
      logSentryException(err);
      console.error("Camera error:", err);

      let errorMessage = "Camera permission denied by browser!";

      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        errorMessage = isMobile ? "Please allow camera access in settings" : "Camera permission denied. Please allow camera access and refresh the page.";
      } else if (err.name === "NotFoundError") {
        errorMessage = "No camera found on your device.";
      } else if (err.name === "NotReadableError") {
        errorMessage = isMobile ? "Camera in use by another app" : "Camera is being used by another application.";
      } else if (err.name === "OverconstrainedError") {
        errorMessage = "Camera doesn't support the required settings.";
      } else if (err.message.includes("system")) {
        errorMessage = "Camera permission denied by system!";
      }

      setcameraPermissiontext(errorMessage);
      setHasPermission(false);
      setShowCamera(false);
      setImgSrc("");
      toast.error(errorMessage, getMobileToastConfig());
    } finally {
      setPhotoLoading(false);
    }
  };

  const triggerRevalidationScheduleOnce = async () => {
    if (!isRevalidationMode) return;
    if (isEditMode) return;
    if (hasScheduledInterview.current) return;
    if (isSchedulingRef.current) return;
    isSchedulingRef.current = true;

    try {
      const refreshedToken = await user?.getIdToken();
      const fetchHeaders = {
        'Authorization': `Bearer ${refreshedToken}`,
        'Content-Type': 'application/json',
      };

      const fetchUserResponse = await axios.post(`${process.env.REACT_APP_FRONTEND}/api/v4.0/candidate-info/fetchuser`, {
        operation: 'fetchuser',
        email: user?.email,
      }, { headers: fetchHeaders });

      const fetchUserData = fetchUserResponse.data?.data?.[0];
      const reqId = userData?.getReqId || fetchUserData?.requisition_id;

      if (!reqId) {
        hasScheduledInterview.current = true;
        return;
      }

      const checkResponse = await axios.post(
        `${process.env.REACT_APP_UPLOAD}/api/v3.0/candidates/check-candidate`,
        JSON.stringify({
          operation: 'checkcandidate',
          checkby: 'email',
          email: user?.email,
          requisition_id: reqId,
        }),
        { headers: fetchHeaders }
      );

      if (checkResponse.data?.isExist === 1) {
        hasScheduledInterview.current = true;
        return;
      }

      const candidateUrl = `${process.env.REACT_APP_CANDIDATE}/getCandidateDetails`;
      const candidateResponse = await axios.post(
        candidateUrl,
        { email: user?.email },
        { headers: fetchHeaders }
      );
      const candidateList = candidateResponse.data?.candidate || [];
      const alreadyScheduled = candidateList.some(
        (c) => Number(c.requisition_id) === Number(reqId)
      );

      if (alreadyScheduled) {
        hasScheduledInterview.current = true;
        return;
      }

      const contactNo = userData?.contactNumber?.replace('+', '') ||
        (fetchUserData?.contact_no ? String(fetchUserData.contact_no).replace('+', '') : '');
      const now = new Date();
      const interviewDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}T${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      const interviewTime = now.toTimeString().slice(0, 8);

      const postData = {
        user_name: user?.displayName,
        email: user?.email,
        added_by_email: 'sheela@virtualemployee.com',
        added_by_name: 'Sheela AI',
        added_by_id: '',
        hrType: '1',
        contact_no: contactNo,
        operation: 'addcandidate',
        resume_url: userData?.resumeUrl || fetchUserData?.resume_url || '',
        request_id: reqId,
        requisition_id: reqId,
        referralCheck: 0,
        is_referral: 0,
        experience: fetchUserData?.total_exp || 0,
        current_ctc: fetchUserData?.other_info?.annual_salary || '',
        technical_round: userData?.getInterviewtype
          ? (userData?.getInterviewtype === 'technical' ? 1 : 0)
          : (fetchUserData?.is_tech_done ?? 0),
        one2one_round: userData?.getInterviewtype
          ? ((userData?.getInterviewtype === 'non-technical' || userData?.getInterviewtype === 'technical') ? 1 : 0)
          : (fetchUserData?.is_onetoone_done ?? 0),
        comm_skill_rating: '',
        cultural_fit_rating: '',
        duration: '',
        audio_url: '',
        relevant_experience: '',
        relevant_exp: '',
        relavant_experience: '',
        expected_ctc: '',
        interview_date: interviewDate,
        interview_time: interviewTime,
        notice_period: fetchUserData?.other_info?.notice_period || '',
        current_company: fetchUserData?.current_company || '',
        source_of_candidate: userData?.source_of_candidate || fetchUserData?.source_of_candidate || 'company_website',
        interview_medium: 'NM Interview',
        job_status: fetchUserData?.job_status || '',
      };

      const scheduleResponse = await axios.post(
        `${process.env.REACT_APP_UPLOAD}/api/v3.0/candidates/add-candidate`,
        postData,
        { headers: { 'Authorization': `Bearer ${refreshedToken}` } }
      );

      if (scheduleResponse.data?.statusCode === 200) {
        await axios.post(
          `${process.env.REACT_APP_UPLOAD}/api/v3.0/requisitions/add-status`,
          {
            requisitionId: reqId,
            name: user?.displayName,
            email: user?.email,
            type: 'candidate',
            status: 'Candidate Added',
            note: `${user?.displayName} has been added by Sheela AI on ${new Date().toISOString().split('T')[0]} for the requisition.`,
            addedBy: 'Sheela AI',
            addedByEmail: 'sheela@virtualemployee.com',
            userType: '1',
          },
          { headers: fetchHeaders }
        );

        const getUrl = `${process.env.REACT_APP_CANDIDATE}/getCandidateDetails`;
        const requestData = { email: user?.email };
        await getInterviewdata(getUrl, requestData, refreshedToken);
      }

      hasScheduledInterview.current = true;
    } catch (error) {
      logSentryException(error);
      console.error('Error in triggerRevalidationScheduleOnce:', error);
    } finally {
      isSchedulingRef.current = false;
    }
  };

  const handleFrozenResumeNext = async () => {
    if (!scheduleInfo?.resume_url) {
      setResumeUploaded(true);
      return;
    }
    try {
      setResumeLoading(true);
      const refreshedToken = await user?.getIdToken();
      await axios.post(`${process.env.REACT_APP_FRONTEND}/api/v4.0/candidate-info/syncFrozenResume`, {
        login_token: user?.uid,
      }, {
        headers: {
          'Authorization': `Bearer ${refreshedToken}`,
          'Content-Type': 'application/json',
        },
      });
    } catch (e) {
      logSentryException(e);
      console.error('Failed to sync frozen resume URL:', e);
    } finally {
      setResumeLoading(false);
      setResumeUploaded(true);
    }
  };

  useEffect(() => {
    if (isHrFrozen && scheduleInfo?.resume_url && !frozenResumeSyncedRef.current) {
      frozenResumeSyncedRef.current = true;
      handleFrozenResumeNext();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHrFrozen, scheduleInfo?.resume_url]);

  const saveProfileInfo = async (actionType, Data, action, onSuccessCallback) => {
    try {
      const refreshedToken = await user?.getIdToken();
      const headers = {
        'Authorization': `Bearer ${refreshedToken}`,
        'Content-Type': 'application/json',
      };

      const submitdata = {
        operation: actionType,
        login_token: user?.uid,
        url: Data,
        action: action
      };
      let url = `${process.env.REACT_APP_FRONTEND}/api/v4.0/candidate-info/updateProfile`;
      await axios.post(url, submitdata, {
        headers: headers,
      });

      if (action === 'updatePhoto') {
        setPhotoLoading(false);
      } else if (action === 'updateResume') {
        setResumeLoading(false);
      }

      if (isRevalidationMode) {
        await triggerRevalidationScheduleOnce();
      }

      dismissCurrentToast();

      showToast('success', 'Record Saved!');
      if (onSuccessCallback) {
        onSuccessCallback();
      } else {
        nextSection();
        nextStep();
      }
    } catch (error) {
      logSentryException(error);
      if (action === 'updatePhoto') {
        setPhotoLoading(false);
      } else if (action === 'updateResume') {
        setResumeLoading(false);
      }
      console.error(error);
      dismissCurrentToast();
      showToast('error', 'Network error!');
    }
  };

  const uploadResume = async (postUploadCallback = null, fileOverride = null) => {
    // Use fileOverride if provided (from handleDrop), otherwise use state
    const fileToUpload = fileOverride || file;

    if (!fileToUpload) {
      const errorMessage = isMobile ?
        'Please upload resume (PDF, DOC, DOCX)' :
        'Please upload resume. File should be PDF, DOC, or DOCX only.';
      showToast('error', errorMessage);
      return;
    }

    const validation = validateResumeFile(fileToUpload);
    if (!validation.isValid) {
      setError(validation.errors[0]);
      showToast('error', validation.errors[0]);
      return;
    }

    let progressToastId = null;

    try {
      setResumeLoading(true);
      setResumeUploadProgress(0);
      setError('');

      progressToastId = showToast('info', isMobile ? 'Uploading resume...' : 'Uploading resume... Please wait.', { autoClose: false });

      const fileName = fileToUpload.name;
      const fileType = fileToUpload.type;

      let uploadResponse = null;
      let uploadSuccessful = false;

      try {
        const refreshedToken = await user?.getIdToken();
        const headers = {
          'Authorization': `Bearer ${refreshedToken}`
        };

        const formData = new FormData();
        formData.append('file', fileToUpload);
        formData.append('bucketName', 'interviewer-profile');
        formData.append('key', `${user?.uid}/Resume/${fileName}`);
        formData.append('contentType', fileType);
        formData.append('isEncoding', false);
        formData.append('ContentEncoding', '');

        const requiredKeys = ['file', 'bucketName', 'key', 'contentType', 'isEncoding'];
        const missingKey = validateFormDataFields(formData, requiredKeys);
        if (missingKey) {
          throw new Error(`Missing required field: ${missingKey}`);
        }

        const response = await uploadWithRetry(
          process.env.REACT_APP_S3UPLOAD,
          formData,
          headers,
          1,
          false,
          setResumeUploadProgress
        );

        if (response.status === 200 && response.data?.uploadResponse) {
          uploadResponse = response.data.uploadResponse;
          uploadSuccessful = true;
          handleUserChange("resumeUrl", uploadResponse)
        } else {
          throw new Error('Primary API upload failed - invalid server response');
        }

      } catch (primaryError) {
        console.error('Primary API failed for resume upload, trying fallback API:', primaryError.message);

        if (progressToastId) {
          toast.dismiss(progressToastId);
          setCurrentToastId(null);
        }
        progressToastId = showToast('info', 'Trying alternative upload method...', { autoClose: false });

        try {
          const refreshedToken = await user?.getIdToken();
          const fallbackHeaders = {
            'Authorization': `Bearer ${refreshedToken}`
          };

          const fallbackFormData = new FormData();
          fallbackFormData.append('file', fileToUpload);
          fallbackFormData.append('bucketName', 'interviewer-profile');
          fallbackFormData.append('key', `${user?.uid}/Resume/${fileName}`);
          fallbackFormData.append('contentType', fileType);
          fallbackFormData.append('isEncoding', false);
          fallbackFormData.append('ContentEncoding', '');

          const fallbackResponse = await uploadWithRetry(
            'https://sheela.io/eye/api/v1.0/system-detail/uploadFileGeneric',
            fallbackFormData,
            fallbackHeaders,
            1,
            true,
            setResumeUploadProgress
          );

          if (fallbackResponse.status === 200 && fallbackResponse.data?.uploadResponse) {
            uploadResponse = fallbackResponse.data.uploadResponse;
            handleUserChange("resumeUrl", uploadResponse)
            uploadSuccessful = true;
          } else {
            throw new Error('Fallback API upload failed - invalid server response');
          }

        } catch (fallbackError) {
          console.error('Fallback API also failed for resume:', fallbackError.message);

          const combinedError = new Error(
            `Both resume upload APIs failed. Primary: ${primaryError.message}. Fallback: ${fallbackError.message}`
          );

          const formDataSummary = {
            primaryError: primaryError.message,
            fallbackError: fallbackError.message,
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
            connectionType: navigator.connection?.effectiveType || 'unknown',
            online: navigator.onLine,
            isMobile: isMobile,
            fileSize: fileToUpload.size,
            fileName: fileName,
            fileType: fileType,
            step: 'resume_upload_with_fallback'
          };

          logToSentry(combinedError, formDataSummary, user);
          throw combinedError;
        }
      }

      if (uploadSuccessful && uploadResponse) {
        if (progressToastId) {
          toast.dismiss(progressToastId);
          setCurrentToastId(null);
          progressToastId = null;
        }

        await new Promise(resolve => setTimeout(resolve, 100));

        await saveProfileInfo("updateUserProfile", uploadResponse, "updateResume", () => {
          setResumeUploaded(true);
          if (postUploadCallback) postUploadCallback();
        });
      }

    } catch (error) {
      console.error('Resume upload error:', error);
      setResumeLoading(false);
      setResumeUploadProgress(0);

      if (progressToastId) {
        toast.dismiss(progressToastId);
        setCurrentToastId(null);
      }

      const formDataSummary = {
        error: error.message,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        connectionType: navigator.connection?.effectiveType || 'unknown',
        online: navigator.onLine,
        isMobile: isMobile,
        fileSize: fileToUpload?.size || 0,
        fileName: fileToUpload?.name || 'unknown',
        fileType: fileToUpload?.type || 'unknown',
        step: 'resume_upload_with_fallback'
      };
      logToSentry(error, formDataSummary, user);

      let errorMessage = getUploadErrorMessage(error, isMobile);
      showToast('error', errorMessage, { autoClose: 6000 });

    } finally {
      setResumeLoading(false);
      setResumeUploadProgress(0);

      if (progressToastId) {
        toast.dismiss(progressToastId);
        setCurrentToastId(null);
      }
    }
  };

  const handleSaveStep1 = () => {
    if (file && !resumeUploaded) {
      uploadResume(nextStep);
    } else {
      nextStep();
    }
  };

  const editNumber = () => {
    // document.getElementById("otpVerification").style.display = "none";
    // document.getElementById("numVerification").style.display = "block";
  };

  const handleOptionChange = (event) => {
    setIswhatsappNumber(event.target.value)
    setSelectedOption(event.target.value);
    logFieldToSentry('WhatsApp number', event.target.value);
  };

  const renderProgressBar = (progress) => (
    <div className={`progress mt-3 ${isMobile ? 'mobile-progress' : ''}`} style={{ height: isMobile ? '12px' : '14px' }}>
      <div
        className="progress-bar progress-bar-striped progress-bar-animated"
        style={{ width: `${progress}%` }}
        role="progressbar"
        aria-valuenow={progress}
        aria-valuemin="0"
        aria-valuemax="100"
      >
        {isMobile && progress > 10 ? `${progress}%` : ''}
      </div>
    </div>
  );

  return (
    <div className='profile-verify-page py-3'>
      <div className="container">

        {/* Step Navigation */}
        <div className="stepbar-wrapper mx-auto mb-3" style={{ maxWidth: isMobile ? '100%' : '600px' }}>
          <ul className="stepper-nav d-flex align-items-center justify-content-between m-0 pb-4">
            {stepLabels.map((title, index) => {
              const stepNumber = index + 1;

              return (
                <li key={stepNumber} className={"text-center " + (currentStep > stepNumber ? "completed" : currentStep === stepNumber ? "active" : "")}
                  style={{ flex: 1 }}
                >
                  <div className="step-count d-flex align-items-center justify-content-center">
                    {currentStep > stepNumber ? "✓" : stepNumber}
                  </div>
                  <div className="step-name">{title}</div>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="stepbar-wrapper mx-auto" style={{ maxWidth: "700px" }}>
          <div className={`steps-card bg-white rounded-4 shadow w-100 text-center`}>
            <form onSubmit={e => e.preventDefault()}>
              {/* Step 1: Photo Upload + Resume Upload + Mobile Verification */}
              {currentStep === 1 && (
                profileInfoLoading ? (
                  <div className="text-center p-4">
                    <div className="spinner-border text-primary mb-3" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted mb-0">Loading your profile...</p>
                  </div>
                ) : (
                  <div className="steps step1-upload">
                    {/* Section 1: Photo Upload */}
                    <div className="card border-0 rounded-3 p-3 mb-3 shadow-sm">
                      <div id="step1" className="user-img-area p-0">
                        <div className="heading-cover mb-3">
                          <div className="main-heading mb-1">{isReviewMode ? 'Review your photo' : 'Upload your photo'}</div>
                          <div className="fs-14 text-muted">{isReviewMode ? 'Your current photo is shown below. You can update it or skip to the next section.' : (isMobile ? 'Show your face clearly' : 'Please make sure your photo clearly shows your face')}</div>
                        </div>
                        <div className="upload-wrapper rounded-4">
                          {hasPermission && imgSrc === "" ?
                            <>
                              <Webcam
                                onUserMedia={handleUserMedia}
                                onUserMediaError={handleUserMediaError}
                                audio={false}
                                videoConstraints={getMobileWebcamConstraints()}
                                width={isMobile ? 180 : 250}
                                height={isMobile ? 180 : 300}
                                ref={webcamRef}
                                screenshotFormat="image/jpeg"
                                className={`user-img img-fluid border border-3 rounded-circle shadow border-secondary mb-1 ${isPhotoLoading ? 'loading' : ''}`}
                                alt="User"
                              />
                              {hasPermission === null && <p className="text-primary fw-medium m-0">{isMobile ? 'Checking camera...' : 'Checking camera permission...'}</p>}
                              {hasPermission === true && <p className="text-success fw-medium m-0"><i className="bi bi-check-circle-fill"></i> {isMobile ? 'Camera ready!' : 'Camera permission granted!'}</p>}
                              {hasPermission === false && <p className="text-danger fw-medium m-0"><i className="bi bi-exclamation-triangle-fill"></i> {getcameraPermissiontext}</p>}
                            </>
                            :
                            imgSrc !== "" ? <img src={imgSrc} className="user-img img-fluid border border-3 rounded-circle shadow border-primary mb-1" alt="User" /> :
                              <>
                                <img src={require('../assets/Avatar.png')} className="user-img img-fluid border border-3 rounded-circle shadow border-primary mb-1" alt="User" />
                                {hasPermission === false && <p className="text-danger fw-medium mt-3"><i className="bi bi-exclamation-triangle-fill"></i> {getcameraPermissiontext}</p>}
                              </>
                          }
                          <div className={`user-name fs-16 fw-medium text-black text-opacity-75 mt-2`}>{user?.displayName}</div>
                          {isPhotoLoading && photoUploadProgress > 0 && renderProgressBar(photoUploadProgress)}
                          {isPhotoLoading ?
                            <div className="mt-2">
                              <div className={`spinner-border text-primary ${isMobile ? 'spinner-border-sm' : ''}`} role='status'>
                                <span className='visually-hidden'>Loading...</span>
                              </div>
                            </div>
                            : null}
                          {showCamera ?
                            <Button variant='primary' size='sm' className={getButtonClasses('rounded-pill mt-2 px-3')} onClick={capture} disabled={isPhotoLoading}>
                              <i className="bi bi-camera-fill me-1"></i>{isMobile ? 'Take Photo' : 'Upload your photo from camera'}
                            </Button>
                            :
                            <Button variant='primary' size='sm' className={getButtonClasses('rounded-pill mt-2 px-3')} onClick={handleTurnOnCamera} disabled={isPhotoLoading}>
                              <i className="bi bi-camera me-1"></i>{isMobile ? 'Turn On Camera' : 'Turn on your webcam'}
                            </Button>
                          }
                          {photoError && (
                            <div className="alert alert-danger mt-3 py-2 px-3 text-start" role="alert">
                              <i className="bi bi-exclamation-triangle-fill me-2"></i>
                              <small>{photoError}</small>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Section 2: Resume Upload */}
                    <div className="card border-0 rounded-3 p-3 mb-3 shadow-sm">
                      <div id="step2" className="p-0">
                        <div className="resume-sec">
                          <div className="heading-cover mb-3">
                            <div className="main-heading mb-1">{isReviewMode ? 'Review your resume' : (isMobile ? 'Upload Resume' : 'Upload your resume')}</div>
                            <div className="fs-14 text-muted">{isReviewMode ? 'Your resume is already uploaded. You can upload a new one or skip to the next section.' : 'Upload your latest resume (PDF, DOC or DOCX).'}</div>
                          </div>

                          {/* Freeze notice: resume uploaded by HR */}
                          {isHrFrozen && scheduleInfo?.resume_url && (
                            <div className="alert alert-warning d-flex align-items-start gap-2 rounded-3 py-2 mb-3" role="alert">
                              <i className="bi bi-lock-fill mt-1"></i>
                              <div>
                                <div>Your resume was uploaded by HR and cannot be changed. To edit the details contact support.</div>
                              </div>
                            </div>
                          )}

                          {/* Frozen resume: view-only with download link */}
                          {isHrFrozen && scheduleInfo?.resume_url && (
                            <div className="alert alert-success d-flex align-items-center justify-content-between gap-2 mb-3 py-2">
                              <span className="d-flex align-items-center gap-2">
                                <i className="bi bi-file-earmark-check-fill fs-5 text-success"></i>
                                <span className="fs-14">🔒 Resume uploaded by HR</span>
                              </span>
                              <a
                                href={scheduleInfo.resume_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-sm btn-outline-success d-flex align-items-center gap-1"
                              >
                                <i className="bi bi-download"></i> Download
                              </a>
                            </div>
                          )}

                          {/* Non-frozen: show normal upload flow */}
                          {!(isHrFrozen && scheduleInfo?.resume_url) && (
                            <>
                              {/* Revalidation: show existing resume status + download */}
                              {isReviewMode && profile?.userfetchedData?.resume_url && !file && (
                                <div className="alert alert-success d-flex align-items-center justify-content-between gap-2 mb-3 py-2">
                                  <span className="d-flex align-items-center gap-2">
                                    <i className="bi bi-file-earmark-check-fill fs-5 text-success"></i>
                                    <span className="fs-14">Resume already uploaded</span>
                                  </span>
                                  <a
                                    href={profile.userfetchedData.resume_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm btn-outline-success d-flex align-items-center gap-1"
                                  >
                                    <i className="bi bi-download"></i> Download
                                  </a>
                                </div>
                              )}

                              <label htmlFor="uploadResume" className="w-100 mb-2">
                                <Dropzone onDrop={handleDrop} />
                                {renderFileStatus()}
                                {error && (
                                  <div className="alert alert-danger mt-3 py-2" role="alert">
                                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                    <small>{error}</small>
                                  </div>
                                )}
                              </label>

                              <div className="fs-12 text-muted mb-3">
                                <i className="bi bi-info-circle me-1"></i>
                                Supported: PDF, DOC, DOCX • Max size: {isMobile ? '3MB' : '5MB'}
                              </div>
                            </>
                          )}
                        </div>

                        {/* Non-frozen: Progress Bar, Terms, Upload button */}
                        {!(isHrFrozen && scheduleInfo?.resume_url) && (
                          <>
                            {isResumeLoading && resumeUploadProgress > 0 && (
                              <div className="upload-progress-section mt-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <small className="text-muted">Uploading resume...</small>
                                  <small className="text-primary fw-medium">{resumeUploadProgress}%</small>
                                </div>
                                <div className={`progress ${isMobile ? 'mobile-progress' : ''}`} style={{ height: isMobile ? '12px' : '14px' }}>
                                  <div
                                    className="progress-bar progress-bar-striped progress-bar-animated bg-warning"
                                    style={{ width: `${resumeUploadProgress}%` }}
                                    role="progressbar"
                                    aria-valuenow={resumeUploadProgress}
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                  >
                                    {isMobile && resumeUploadProgress > 15 ? `${resumeUploadProgress}%` : ''}
                                  </div>
                                </div>
                                {resumeUploadProgress > 80 && (
                                  <small className="text-success mt-1 d-block">
                                    <i className="bi bi-check-circle me-1"></i>
                                    Almost done...
                                  </small>
                                )}
                              </div>
                            )}

                            {!navigator.onLine && (
                              <div className="alert alert-warning mt-3 py-2" role="alert">
                                <i className="bi bi-wifi-off me-2"></i>
                                <small>No internet connection. Please check your network.</small>
                              </div>
                            )}
                          </>
                        )}


                      </div>
                    </div>

                    {/* Section 3: Mobile Verification */}
                    <div className="card border-0 rounded-3 p-3 mb-3 shadow-sm">
                      <div className="mob-num-verify p-0">
                        {/* HR-frozen phone: never editable, OTP required if not verified */}
                        {isHrFrozen && scheduleInfo?.contact_no ? (
                          <div className="num-verification">
                            <div className="upload-icon mb-3">
                              <span className="bg-primary bg-opacity-10 fs-3 d-flex align-items-center justify-content-around rounded-3 mx-auto" style={{ height: '55px', width: '55px' }}>📱</span>
                            </div>

                            <div className="heading-cover mb-4">
                              <div className="main-heading mb-2">🔒 Phone Number</div>
                              <div className="fs-14 text-muted">Your phone number was provided by HR and cannot be changed. To edit the details contact support.</div>
                            </div>

                            <div className="alert alert-warning d-flex align-items-start gap-2 rounded-3 py-2 mb-3" role="alert">
                              <i className="bi bi-lock-fill mt-1"></i>
                              <div>
                                <div>This number was set by your HR and cannot be edited. To edit the details contact support.</div>
                              </div>
                            </div>

                            <div className={`alert ${hrNumVerified || isVerificationCompleted ? 'alert-success' : 'alert-secondary'} d-flex align-items-center gap-2 mb-3 py-2`}>
                              <i className={`bi ${hrNumVerified || isVerificationCompleted ? 'bi-check-circle-fill fs-5 text-success' : 'bi-telephone-fill fs-5'}`}></i>
                              <span className="fs-14">
                                Phone: +{String(scheduleInfo.contact_no).replace(/[^\d]/g, '')}
                                {(hrNumVerified || isVerificationCompleted) && <span className="ms-2 badge bg-success">✓ Verified</span>}
                              </span>
                            </div>

                            {/* Case 1: Number NOT verified — show OTP flow */}
                            {!hrNumVerified && !isVerificationCompleted && (
                              <>
                                {/* Sub-state A: Show Send OTP prompt */}
                                {isNumVerificationCompleted && !isOTPVerificationCompleted && (
                                  <>
                                    <div className="fs-14 text-muted mb-3">
                                      Your number hasn't been verified yet. Please verify via OTP to continue.
                                    </div>

                                    <div className="alert alert-success rounded-3 d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3 py-2">
                                      <span className="small d-flex align-items-center gap-1"><img src={whatsappIcon} alt="whatsapp Icon" className="img-fluid" /> Is this number active on WhatsApp?</span>
                                      <div className="radio-wrapper">
                                        <div className="form-check form-check-inline mb-0">
                                          <input className="form-check-input border border-secondary border-opacity-75" type="radio" value="Yes" checked={selectedOption === "Yes"} onChange={handleOptionChange} name="whatsappNum" id="frozenYes" />
                                          <label className="form-check-label" htmlFor="frozenYes">Yes</label>
                                        </div>
                                        <div className="form-check form-check-inline mb-0">
                                          <input className="form-check-input border border-secondary border-opacity-75" type="radio" value="No" checked={selectedOption === "No"} onChange={handleOptionChange} name="whatsappNum" id="frozenNo" />
                                          <label className="form-check-label" htmlFor="frozenNo">No</label>
                                        </div>
                                      </div>
                                    </div>

                                    <Button variant='primary' className={getButtonClasses('btn-custom d-inline-flex align-items-center justify-content-center gap-2')} onClick={generateOtp} disabled={isMobileVerificationLoading || selectedOption === ""}>
                                      {isMobile ? 'Send OTP' : 'Send Verification Code'}
                                      {isMobileVerificationLoading ? <span className="spinner-border spinner-border-sm text-light" role="status"><span className='visually-hidden'>Loading...</span></span> : <i className="bi bi-arrow-right"></i>}
                                    </Button>
                                  </>
                                )}

                                {/* Sub-state B: OTP input */}
                                {isOTPVerificationCompleted && (
                                  <div className="otp-verification">
                                    <div className="heading-cover mb-3">
                                      <div className="fs-14 text-muted">
                                        We sent a 4-digit code to +{String(scheduleInfo.contact_no).replace(/[^\d]/g, '')}
                                      </div>
                                    </div>

                                    <div className={`enter-otp input-cover d-flex justify-content-center ${isMobile ? 'gap-2' : 'gap-2'} mb-4`}>
                                      {otp.map((digit, index) => (
                                        <input
                                          key={index}
                                          type="text"
                                          inputMode="numeric"
                                          pattern="[0-9]*"
                                          autoComplete="one-time-code"
                                          maxLength={1}
                                          value={digit}
                                          name={`ssn-${index + 1}`}
                                          className="form-control fw-semibold shadow-sm text-dark text-center w-100 py-2"
                                          onChange={(event) => handleChange(event, index)}
                                          onKeyDown={(event) => handleKeyDown(event, index)}
                                          style={{ fontSize: isMobile ? '1.2rem' : '1.5rem' }}
                                        />
                                      ))}
                                    </div>

                                    <div className="mb-4 small">
                                      {isMobile ? "Didn't receive? " : "Didn't receive the code? "}
                                      <span className="text-primary fw-medium pointer" onClick={generateOtp}>Resend Code</span>
                                    </div>

                                    <Button className={getButtonClasses('btn-custom  d-inline-flex align-items-center justify-content-center gap-2')} onClick={handleSubmit} disabled={isMobileVerificationLoading || otp.join('').length !== 4}>
                                      {isMobile ? 'Verify' : 'Verify OTP'}
                                      {isMobileVerificationLoading ? <span className="spinner-border spinner-border-sm text-light" role="status"><span className='visually-hidden'>Loading...</span></span> : <i className="bi bi-arrow-right"></i>}
                                    </Button>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        ) : isReviewMode && phone && phone.length > 5 ? (
                          <div className="num-verification">
                            <div className="upload-icon mb-3">
                              <span className="bg-primary bg-opacity-10 fs-3 d-flex align-items-center justify-content-around rounded-3 mx-auto" style={{ height: '55px', width: '55px' }}>📱</span>
                            </div>

                            <div className="heading-cover mb-3">
                              <div className={`main-heading mb-2`}>Review your phone number</div>
                              <div className="fs-14 text-muted">Below is your verified phone number for your review.</div>
                            </div>

                            <div className="alert alert-success d-flex align-items-center gap-2 mb-3 py-2">
                              <i className="bi bi-telephone-fill fs-5 text-success"></i>
                              <span className="fs-14">Phone verified: +{phone}</span>
                            </div>
                          </div>
                        ) : (
                          <>
                            {isNumVerificationCompleted && (
                              <div id="numVerification" className="num-verification">
                                <div className="upload-icon mb-3">
                                  <span className="bg-primary bg-opacity-10 fs-3 d-flex align-items-center justify-content-around rounded-3 mx-auto" style={{ height: '55px', width: '55px' }}>📱</span>
                                </div>

                                <div className="heading-cover mb-4">
                                  <div className={`main-heading mb-2`}>{isMobile ? 'Phone Number' : 'Enter your phone number'}</div>
                                  <div className="fs-14 text-muted">We'll send a 4-digit OTP to verify your number.<br /> This number will also be used to log into the interview app.</div>
                                </div>

                                <div className="input-cover mb-3">
                                  <PhoneInput
                                    inputProps={{
                                      name: 'phone',
                                      required: true,
                                      autoFocus: false,
                                      autoComplete: 'off'
                                    }}
                                    country={"in"}
                                    value={phone}
                                    onChange={handleOnChange}
                                    enableSearch={true}
                                    countryCodeEditable={false}
                                    inputClass="w-100 shadow-sm py-2"
                                    dropdownClass="text-start"
                                  />
                                </div>

                                {phone && phone.length > 5 && (
                                  <div className="mb-3">
                                    <div className={`fs-14 text-${validatePhoneNumber(phone) ? 'success' : 'primary'}`}>
                                      <i className={`bi bi-${validatePhoneNumber(phone) ? 'check' : 'exclamation'}-circle me-1`}></i>
                                      {validatePhoneNumber(phone) ? 'Phone number format looks good' : 'Please check phone number format'}
                                    </div>
                                    {!validatePhoneNumber(phone) && (
                                      <div className="mt-1">
                                        <small className="text-muted d-block">
                                          Expected format: +{country?.dialCode || '91'} followed by digits
                                        </small>
                                      </div>
                                    )}
                                  </div>
                                )}

                                <div className="alert alert-success rounded-3 d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3 py-2">
                                  <span className="small d-flex align-items-center gap-1"><img src={whatsappIcon} alt="whatsapp Icon" className="img-fluid" /> Is this number active on WhatsApp?</span>
                                  <div className="radio-wrapper">
                                    <div className="form-check form-check-inline mb-0">
                                      <input className="form-check-input border border-secondary border-opacity-75" type="radio" value="Yes" checked={selectedOption === "Yes"} onChange={handleOptionChange} name="whatsappNum" id="Yes" />
                                      <label className="form-check-label" htmlFor="Yes">Yes</label>
                                    </div>
                                    <div className="form-check form-check-inline mb-0">
                                      <input className="form-check-input border border-secondary border-opacity-75" type="radio" value="No" checked={selectedOption === "No"} onChange={handleOptionChange} name="whatsappNum" id="No" />
                                      <label className="form-check-label" htmlFor="No">No</label>
                                    </div>
                                  </div>
                                </div>

                                <Button variant='primary' className={getButtonClasses('btn-custom d-inline-flex align-items-center justify-content-center gap-2')} onClick={generateOtp} disabled={isMobileVerificationLoading || !validatePhoneNumber(phone) || selectedOption === ""}>
                                  {isMobile ? 'Send OTP' : 'Send Verification Code'}
                                  {isMobileVerificationLoading ? <span className="spinner-border spinner-border-sm text-light" role="status"><span className='visually-hidden'>Loading...</span></span> : <i className="bi bi-arrow-right"></i>}
                                </Button>
                              </div>
                            )}
                          </>)}
                        {isOTPVerificationCompleted && !(isHrFrozen && scheduleInfo?.contact_no) && (
                          <div id="otpVerification" className="otp-verification">
                            <div className="upload-icon mb-3">
                              <span className="bg-primary bg-opacity-10 fs-3 d-flex align-items-center justify-content-around rounded-3 mx-auto" style={{ height: '55px', width: '55px' }}>
                                <i className="bi bi-chat-dots fs-3 text-primary"></i>
                              </span>
                            </div>

                            <div className="heading-cover mb-4">
                              <div className="main-heading mb-2">Enter Verification Code</div>
                              <div className="fs-14 text-muted">
                                {isMobile ? `We sent a 4-digit code to +${phone}` : `We sent a 4-digit code to +${phone}`}
                                <i className="bi bi-pencil-fill ms-2 fs-6 pointer text-primary" onClick={editNumber}></i>
                              </div>
                            </div>

                            <div className={`enter-otp input-cover d-flex justify-content-center ${isMobile ? 'gap-2' : 'gap-2'} mb-4`}>
                              {otp.map((digit, index) => (
                                <input
                                  key={index}
                                  type="text"
                                  inputMode="numeric"
                                  pattern="[0-9]*"
                                  autoComplete="one-time-code"
                                  maxLength={1}
                                  value={digit}
                                  name={`ssn-${index + 1}`}
                                  className="form-control fw-semibold shadow-sm text-dark text-center w-100 py-2"
                                  onChange={(event) => handleChange(event, index)}
                                  onKeyDown={(event) => handleKeyDown(event, index)}
                                  style={{
                                    fontSize: isMobile ? '1.2rem' : '1.5rem'
                                  }}
                                />
                              ))}
                            </div>

                            <div className="mb-4 small">
                              {isMobile ? "Didn't receive? " : "Didn't receive the code? "}
                              <span className="text-primary fw-medium pointer" onClick={generateOtp}>Resend Code</span>
                            </div>

                            <Button className={getButtonClasses('btn-custom  d-inline-flex align-items-center justify-content-center gap-2')} onClick={handleSubmit} disabled={isMobileVerificationLoading || otp.join('').length !== 4}>
                              {isMobile ? 'Verify' : 'Verify OTP'}
                              {isMobileVerificationLoading ? <span className="spinner-border spinner-border-sm text-light" role="status"><span className='visually-hidden'>Loading...</span></span> : <i className="bi bi-arrow-right"></i>}
                            </Button>
                          </div>
                        )}

                        {isVerificationCompleted && !(isHrFrozen && scheduleInfo?.contact_no) && (
                          <div id="profileVerified" className="profile-verified">
                            <div className="upload-icon mb-3">
                              <span className="bg-primary bg-opacity-10 fs-3 d-flex align-items-center justify-content-around rounded-3 mx-auto" style={{ height: '55px', width: '55px' }}>✅</span>
                            </div>

                            <div className="heading-cover mb-3">
                              <div className="main-heading mb-1">Profile Verified!</div>
                              <div className="fs-14 text-muted">Your photo, CV, and phone number have all been verified.<br /> Now let's add your details to unlock your interview!</div>
                            </div>

                            <div className="info-box border border-primary border-opacity-50 rounded-4 mb-3 mb-xxl-4 p-3 p-xxl-4">
                              <div className="fs-base fw-bold text-primary text-uppercase text-start mb-2 pb-1">What's next?</div>
                              <div className="fs-14 text-dark d-flex flex-column gap-2 text-start">
                                <div className="d-flex align-items-center gap-2"><i className="bi bi-briefcase-fill fs-16 text-primary"></i> Add your employment details</div>
                                <div className="d-flex align-items-center gap-2"><i className="bi bi-mortarboard-fill fs-16 text-primary"></i> Add your education background</div>
                                <div className="d-flex align-items-center gap-2"><i className="bi bi-chat-dots-fill fs-16 text-primary"></i> Write a quick profile headline</div>
                                <div className="d-flex align-items-center gap-2"><i className="bi bi-cloud-download-fill fs-16 text-primary"></i> Download the app & start your interview!</div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Step 1 Action Buttons */}
                    <div className="btn-wrapper text-center mt-2 mb-4">
                      <div className="d-flex gap-2">
                        {isEditMode || isRevalidationMode ? (
                          <>
                            {/* Edit Mode: Save (enabled only when changed) + Next (always enabled) */}
                            {/* <Button variant='primary' className={getButtonClasses('btn-custom flex-grow-1 d-inline-flex align-items-center justify-content-center gap-2')}
                            onClick={handleSaveStep1}
                            disabled={isAnyStep1OperationLoading || !hasStep1Changed}>
                            Save
                            {isAnyStep1OperationLoading && <span className="spinner-border spinner-border-sm text-light" role="status"><span className="visually-hidden">Loading...</span></span>}
                          </Button> */}
                            <Button variant='outline-primary' className={getButtonClasses('btn-custom flex-grow-1 d-inline-flex align-items-center justify-content-center gap-1')}
                              onClick={nextStep}
                              disabled={isAnyStep1OperationLoading}>
                              Next <i className="bi bi-arrow-right"></i>
                            </Button>
                          </>
                        ) : (
                          /* Normal / Revalidation / HR Freeze Mode: Save and Continue enabled only when all 3 conditions met */
                          <Button variant='primary' className={getButtonClasses('btn-custom flex-grow-1 d-inline-flex align-items-center justify-content-center gap-2')}
                            onClick={handleSaveStep1}
                            disabled={isAnyStep1OperationLoading || !isStep1Valid}>
                            Save and Continue
                            {/* {isAnyStep1OperationLoading && <span className="spinner-border spinner-border-sm text-light" role="status"><span className="visually-hidden">Loading...</span></span>}
                          {!isAnyStep1OperationLoading && <i className="bi bi-arrow-right"></i>} */}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                )
              )}

              {/* Step 2: Professional Details */}
              {currentStep === 2 && (
                <CompleteProfile
                  nextStep={nextStep}
                  currentStep={currentStep}
                  setCurrentStep={setCurrentStep}
                />
              )}
            </form>
          </div>
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={2000} hideProgressBar={false} theme="colored" />
    </div>
  )
}